// src/pages/settings/notification-management-page/components/RecipientDisplay.tsx
import React, { useState, useEffect } from 'react';
import { Button, Tooltip } from 'antd';
import { UserOutlined } from '@ant-design/icons';
import { ApiRequest } from '../../../../services/api/api';

// Module-level cache for employees (shared with notification-management-column-model)
let moduleCachedEmployees: Array<{ value: number; label: string }> = []
let moduleEmployeesLoaded = false
let moduleEmployeesLoading = false
const employeeDataListeners: Array<(data: any) => void> = []

const loadEmployeesModule = async () => {
  if (moduleEmployeesLoaded || moduleEmployeesLoading) {
    return moduleCachedEmployees
  }

  moduleEmployeesLoading = true
  try {
    const result = await ApiRequest({
      otherUrl: 'Employee/GetAllEmployee',
      type: 'get',
    })
    const employees = result?.data?.data ?? result?.data ?? []
    moduleCachedEmployees = (employees || []).map((emp: any) => {
      const firstName = emp.firstName ?? emp.FirstName ?? ''
      const lastName = emp.lastName ?? emp.LastName ?? ''
      const name = `${firstName} ${lastName}`.trim()
      return {
        value: emp.employeeId ?? emp.EmployeeId ?? 0,
        label: name || emp.userName || emp.UserName || `Employee ${emp.employeeId ?? emp.EmployeeId}`,
      }
    })
    moduleEmployeesLoaded = true
    console.log('RecipientDisplay: Employees loaded to module:', moduleCachedEmployees.length)

    employeeDataListeners.forEach(listener => listener(moduleCachedEmployees))
    employeeDataListeners.length = 0
  } catch (error) {
    console.error('RecipientDisplay: Failed to load employees', error)
  } finally {
    moduleEmployeesLoading = false
  }

  return moduleCachedEmployees
}

// Start loading immediately
loadEmployeesModule()

interface RecipientDisplayProps {
  employeeIds: number[] | null | undefined;
  allEmployees: Array<{ value: number; label: string }>;
  onEdit: () => void;
  isEditing?: boolean;
}

const RecipientDisplay: React.FC<RecipientDisplayProps> = ({
  employeeIds,
  allEmployees,
  onEdit,
  isEditing = false
}) => {
  const [localEmployees, setLocalEmployees] = useState<Array<{ value: number; label: string }>>([])

  useEffect(() => {
    if (moduleEmployeesLoaded) {
      setLocalEmployees([...moduleCachedEmployees])
    } else if (moduleEmployeesLoading) {
      employeeDataListeners.push((data) => setLocalEmployees([...data]))
    } else {
      loadEmployeesModule().then(data => setLocalEmployees([...data]))
    }
  }, [])

  // Use local employees if available, otherwise use prop (for backward compatibility)
  const employees = localEmployees.length > 0 ? localEmployees : allEmployees

  const isAll = !employeeIds || employeeIds.length === 0;

  console.log('RecipientDisplay render:', { employeeIds, localEmployeesLength: localEmployees.length, propEmployeesLength: allEmployees.length, finalLength: employees.length, isEditing })

  if (isAll) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <UserOutlined style={{ color: '#1890ff' }} />
        <span>All Employees</span>
        {isEditing && (
          <Button type="link" size="small" onClick={onEdit}>
            Edit
          </Button>
        )}
      </div>
    );
  }

  // Find selected employees from employees
  const selectedEmployees = employees.filter(emp =>
    employeeIds?.includes(emp.value)
  );

  console.log('RecipientDisplay selectedEmployees:', selectedEmployees.length, 'out of', employeeIds.length)

  // Show first 4 employees
  const displayCount = 4
  const displayNames = selectedEmployees.slice(0, displayCount).map(emp => emp.label);
  const additionalCount = Math.max(0, selectedEmployees.length - displayCount);

  const displayText = displayNames.length > 0
    ? displayNames.join(', ') + (additionalCount > 0 ? ` ... (+${additionalCount})` : '')
    : employees.length === 0
      ? `${employeeIds.length} employees`
      : 'No employees selected';

  // Create tooltip content
  const tooltipContent = selectedEmployees.length > 0
    ? selectedEmployees.map(emp => emp.label).join('\n')
    : `${employeeIds.length} employees`

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      <UserOutlined style={{ color: '#1890ff' }} />
      <Tooltip
        title={tooltipContent}
        placement="top"
        overlayStyle={{ whiteSpace: 'pre-line' }}
      >
        <span style={{ cursor: isEditing ? 'default' : 'help' }}>
          {displayText}
        </span>
      </Tooltip>
      {isEditing && (
        <Button type="link" size="small" onClick={onEdit}>
          Edit
        </Button>
      )}
    </div>
  );
};

export default RecipientDisplay;
